import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '../../ui';
import { ConfirmModal } from '../../Modal/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../hooks/useToast';
import {
  useClusteringBatches,
  useClusteringProject,
  useDeleteBatch,
} from '../../../hooks/useClustering';
import { canMutate, formatDate, formatNumber, formatUsd } from '../shared';
import { BatchStatusBadge } from './BatchStatusBadge';
import { userMessage } from '../../../utils/userMessage';
import styles from '../Clustering.module.css';

interface BatchesViewProps {
  projectId: string;
}

function readConfigString(config: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = config?.[key];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function readConfigNumber(config: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = config?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return String(parsed);
  }
  return null;
}

export function BatchesView({ projectId }: BatchesViewProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const mutate = canMutate(user);

  const { data: project } = useClusteringProject(projectId);
  const {
    data: batches = [],
    isLoading,
    error,
  } = useClusteringBatches({ project_id: projectId, limit: 100, offset: 0 });

  const deleteMut = useDeleteBatch();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success('Batch deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error('Delete failed', (err as Error).message);
    }
  };

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate('/clustering')}
      >
        ← Back to projects
      </button>

      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{project?.name ?? 'Batches'}</h1>
          <p className={styles.subtitle}>
            Clustering runs for this project. Open one to see progress or results.
          </p>
        </div>
        {mutate && (
          <div className={styles.headerActions}>
            <Button
              variant="primary"
              onClick={() => navigate(`/clustering/${projectId}/create`)}
              data-testid="clustering-new-batch"
            >
              + New batch
            </Button>
          </div>
        )}
      </div>

      {error && <div className={styles.error}>{userMessage(error, 'Could not load your batches. Please try again.')}</div>}

      {isLoading ? (
        <div className={styles.loading}>Loading batches…</div>
      ) : batches.length === 0 ? (
        <Card padding="lg">
          <div className={styles.empty}>
            <h3>No batches yet</h3>
            <p>Submit a keyword list and we'll cluster it by SERP overlap.</p>
            {mutate && (
              <Button
                variant="primary"
                onClick={() => navigate(`/clustering/${projectId}/create`)}
              >
                + New batch
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className={styles.grid}>
          {batches.map((b) => {
            const country = readConfigString(b.config, 'country') ?? '—';
            const website = readConfigString(b.config, 'target_domain') ?? '—';
            const threshold = readConfigNumber(b.config, 'threshold') ?? '—';

            return (
              <Card
                key={b.id}
                variant="outlined"
                padding="md"
                hoverable
                onClick={() =>
                  navigate(`/clustering/${projectId}/batches/${b.id}`)
                }
                data-testid={`clustering-batch-${b.id}`}
              >
                <div className={styles.cardBody}>
                  <div className={styles.cardFooter}>
                    <h3 className={styles.cardTitle}>
                      {b.name ?? `Batch ${b.id.slice(0, 8)}`}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <BatchStatusBadge status={b.status} />
                      {mutate && (
                        <button
                          type="button"
                          title="Delete batch"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px 4px',
                            color: 'var(--color-text-muted, #9ca3af)',
                            fontSize: '14px',
                            lineHeight: 1,
                            borderRadius: '4px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: b.id, name: b.name ?? `Batch ${b.id.slice(0, 8)}` });
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  <div className={styles.cardMeta}>
                    <span>{formatNumber(b.total_keywords)} keywords</span>
                    <span>{formatNumber(b.clusters_count)} clusters</span>
                    <span>{formatUsd(b.cost_usd_actual || b.cost_usd_estimated)}</span>
                  </div>
                  <div className={styles.cardMeta}>
                    <span>Country: {country}</span>
                    <span>Website: {website}</span>
                    <span>Threshold: {threshold}</span>
                  </div>
                  <div className={styles.cardMeta}>
                    <span>Started {formatDate(b.started_at ?? b.created_at)}</span>
                    {b.completed_at && (
                      <span>Completed {formatDate(b.completed_at)}</span>
                    )}
                  </div>
                  {b.error_message && (
                    <div className={styles.error} style={{ marginBottom: 0 }}>
                      {userMessage(b.error_message, 'This batch could not be completed. Please try again.')}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete batch?"
        message={`"${deleteTarget?.name}" and all its clusters, keywords, and results will be permanently removed. This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}
